import { useState, useRef, useEffect, useCallback } from "react";
import { useController } from "react-hook-form";
import { cn } from "@utils";
import TokenDropdown, { TokenIdleButton, getCaretRect } from "./TokenDropdown";
import useTemplateTokens from "./useTemplateTokens";

function composeValidation(external, tokenValidator) {
  if (!external) return tokenValidator;
  if (typeof external === "function") {
    return (val) => {
      const result = external(val);
      if (result !== true && result !== undefined) return result;
      return tokenValidator(val);
    };
  }
  if (typeof external === "object") {
    return { ...external, invalidTokens: tokenValidator };
  }
  return tokenValidator;
}

const TemplateInput = ({
  name,
  control,
  displayName,
  required,
  placeholder,
  invalid,
  validate,
  innerRef,
  disabled,
  className,
  onFocus,
  onSelect,
  onChange: onValueChange,
  blacklistedTokens,
  borderless = false,
  wrap = false,
  maxLength,
  style,
  rich = false,
}) => {
  const editorRef = useRef(null);
  const containerRef = useRef(null);
  const lastValueRef = useRef("");
  const savedRangeRef = useRef(null);
  const replaceTargetRef = useRef(null);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [caretPos, setCaretPos] = useState({ top: 0, left: 0 });
  const [showPlaceholder, setShowPlaceholder] = useState(true);

  const {
    availableTokens,
    serialize,
    deserialize,
    serializeRich,
    deserializeRich,
    createTokenElement,
    validateTokens,
  } = useTemplateTokens(blacklistedTokens);

  const { field } = useController({
    name,
    control,
    rules: {
      required: required ? `${displayName} is required` : null,
      validate: composeValidation(validate, validateTokens),
    },
  });

  const fieldOnChangeRef = useRef(field.onChange);
  fieldOnChangeRef.current = field.onChange;
  const onValueChangeRef = useRef(onValueChange);
  onValueChangeRef.current = onValueChange;

  const resetIdleTimer = useCallback(() => {
    editorRef.current?.__resetIdleTimer?.();
  }, []);

  const renderContent = useCallback(
    (text) => {
      const editor = editorRef.current;
      if (!editor) return;
      editor.innerHTML = "";
      editor.appendChild(rich ? deserializeRich(text || "") : deserialize(text || ""));
      setShowPlaceholder(!text);
    },
    [deserialize, deserializeRich, rich],
  );

  useEffect(() => {
    const val = field.value || "";
    if (val !== lastValueRef.current) {
      renderContent(val);
      lastValueRef.current = val;
    }
  }, [field.value, renderContent]);

  const syncValue = useCallback(() => {
    const editor = editorRef.current;
    if (!editor) return;
    const plainText = serialize(editor);
    if (maxLength && plainText.length > maxLength) {
      renderContent(lastValueRef.current);
      const sel = window.getSelection();
      const range = document.createRange();
      range.selectNodeContents(editor);
      range.collapse(false);
      sel.removeAllRanges();
      sel.addRange(range);
      return;
    }
    const value = rich ? serializeRich(editor) : plainText;
    lastValueRef.current = value;
    fieldOnChangeRef.current(value);
    onValueChangeRef.current?.(value);
    setShowPlaceholder(!plainText);
    resetIdleTimer();
  }, [serialize, serializeRich, rich, maxLength, resetIdleTimer, renderContent]);

  const getCaretOrContainerPos = useCallback(() => {
    const cRect = containerRef.current?.getBoundingClientRect();
    if (!cRect) return { top: 0, lineTop: 0, left: 0, viewportTop: 0 };
    const rect = getCaretRect(editorRef.current);
    if (rect) {
      return {
        top: rect.bottom - cRect.top,
        lineTop: rect.top - cRect.top,
        left: rect.left - cRect.left,
        viewportTop: rect.top,
      };
    }
    return { top: cRect.height, lineTop: 0, left: 0, viewportTop: cRect.top };
  }, []);

  const insertToken = useCallback(
    (token) => {
      const editor = editorRef.current;
      if (!editor) return;

      if (
        replaceTargetRef.current &&
        editor.contains(replaceTargetRef.current)
      ) {
        const oldToken = replaceTargetRef.current;
        const newToken = createTokenElement(token.value, token.name, true);
        oldToken.parentNode.replaceChild(newToken, oldToken);
        replaceTargetRef.current = null;
        setDropdownOpen(false);
        syncValue();
        editor.focus();
        return;
      }

      if (maxLength) {
        const current = serialize(editor);
        if (current.length + token.value.length > maxLength) return;
      }

      editor.focus();
      const sel = window.getSelection();
      let range;

      if (savedRangeRef.current) {
        range = savedRangeRef.current;
        savedRangeRef.current = null;
        sel.removeAllRanges();
        sel.addRange(range);
        range.deleteContents();
      } else if (sel.rangeCount > 0 && editor.contains(sel.anchorNode)) {
        range = sel.getRangeAt(0);
        range.deleteContents();
      } else {
        range = document.createRange();
        range.selectNodeContents(editor);
        range.collapse(false);
      }

      const tokenEl = createTokenElement(token.value, token.name, true);
      const spacer = document.createTextNode("\u200B");

      range.insertNode(spacer);
      range.insertNode(tokenEl);

      setDropdownOpen(false);
      syncValue();

      // Restore focus + caret after React re-renders (dropdown unmount can
      // disrupt the browser's selection inside contentEditable).
      requestAnimationFrame(() => {
        editor.focus();
        const s = window.getSelection();
        const r = document.createRange();
        r.setStart(spacer, spacer.textContent.length);
        r.collapse(true);
        s.removeAllRanges();
        s.addRange(r);
      });
    },
    [createTokenElement, syncValue, serialize, maxLength],
  );

  const handleKeyDown = useCallback(
    (e) => {
      if (e.key === "{") {
        e.preventDefault();
        const sel = window.getSelection();
        if (sel.rangeCount > 0) {
          savedRangeRef.current = sel.getRangeAt(0).cloneRange();
        }
        setCaretPos(getCaretOrContainerPos());
        setDropdownOpen(true);
        return;
      }

      if (e.key === "Enter") {
        e.preventDefault();
        return;
      }

      if (e.key === "Backspace" || e.key === "Delete") {
        const sel = window.getSelection();
        if (!sel.rangeCount || !sel.isCollapsed) return;

        const { startContainer, startOffset } = sel.getRangeAt(0);
        let tokenToDelete = null;

        if (e.key === "Backspace") {
          if (startContainer.nodeType === Node.TEXT_NODE && startOffset === 0) {
            const prev = startContainer.previousSibling;
            if (prev?.dataset?.token) tokenToDelete = prev;
          } else if (startContainer === editorRef.current) {
            const child = startContainer.childNodes[startOffset - 1];
            if (child?.dataset?.token) tokenToDelete = child;
          }
        } else {
          if (
            startContainer.nodeType === Node.TEXT_NODE &&
            startOffset === startContainer.textContent.length
          ) {
            const next = startContainer.nextSibling;
            if (next?.dataset?.token) tokenToDelete = next;
          } else if (startContainer === editorRef.current) {
            const child = startContainer.childNodes[startOffset];
            if (child?.dataset?.token) tokenToDelete = child;
          }
        }

        if (tokenToDelete) {
          e.preventDefault();
          tokenToDelete.remove();
          syncValue();
        }
      }
    },
    [syncValue, getCaretOrContainerPos],
  );

  const handleEditorClick = useCallback(
    (e) => {
      const tokenEl = e.target.closest?.("[data-token]");
      if (tokenEl && editorRef.current?.contains(tokenEl)) {
        replaceTargetRef.current = tokenEl;
        savedRangeRef.current = null;
        const rect = tokenEl.getBoundingClientRect();
        const cRect = containerRef.current?.getBoundingClientRect();
        setCaretPos({
          top: rect.bottom - (cRect?.top ?? 0),
          lineTop: rect.top - (cRect?.top ?? 0),
          left: rect.left - (cRect?.left ?? 0),
          viewportTop: rect.top,
        });
        setDropdownOpen(true);
      } else {
        resetIdleTimer();
      }
    },
    [resetIdleTimer],
  );

  const handleIdleButtonActivate = useCallback(() => {
    const sel = window.getSelection();
    if (sel.rangeCount > 0 && editorRef.current?.contains(sel.anchorNode)) {
      savedRangeRef.current = sel.getRangeAt(0).cloneRange();
    }
    setCaretPos(getCaretOrContainerPos());
    setDropdownOpen(true);
  }, [getCaretOrContainerPos]);

  const serializeFragment = useCallback(
    (fragment) => {
      const temp = document.createElement("div");
      temp.appendChild(fragment);
      return serialize(temp);
    },
    [serialize],
  );

  const handleCopy = useCallback(
    (e) => {
      const sel = window.getSelection();
      if (!sel.rangeCount) return;
      const cloned = sel.getRangeAt(0).cloneContents();
      const text = serializeFragment(cloned);
      e.clipboardData.setData("text/plain", text);
      e.preventDefault();
    },
    [serializeFragment],
  );

  const handleCut = useCallback(
    (e) => {
      handleCopy(e);
      const sel = window.getSelection();
      if (!sel.rangeCount) return;
      sel.getRangeAt(0).deleteContents();
      syncValue();
    },
    [handleCopy, syncValue],
  );

  const handlePaste = useCallback(
    (e) => {
      e.preventDefault();
      let text = e.clipboardData.getData("text/plain").replace(/\n/g, " ");
      const sel = window.getSelection();
      if (!sel.rangeCount) return;
      const range = sel.getRangeAt(0);
      range.deleteContents();
      if (maxLength) {
        const remaining = maxLength - serialize(editorRef.current).length;
        if (remaining <= 0) return;
        text = text.slice(0, remaining);
      }
      const fragment = deserialize(text);
      const lastNode = fragment.lastChild;
      range.insertNode(fragment);
      if (lastNode) {
        range.setStartAfter(lastNode);
        range.collapse(true);
      }
      sel.removeAllRanges();
      sel.addRange(range);
      syncValue();
      const editor = editorRef.current;
      if (editor) editor.scrollLeft = editor.scrollWidth;
    },
    [syncValue, deserialize, serialize, maxLength],
  );

  useEffect(() => {
    if (!onSelect) return;
    const handler = () => {
      const sel = window.getSelection();
      if (editorRef.current?.contains(sel.anchorNode)) {
        onSelect({ target: editorRef.current });
      }
    };
    document.addEventListener("selectionchange", handler);
    return () => document.removeEventListener("selectionchange", handler);
  }, [onSelect]);

  return (
    <div
      ref={containerRef}
      className={cn("relative w-full font-sans text-sm leading-normal")}
    >
      {showPlaceholder && placeholder && (
        <div
          className={cn(
            "absolute inset-0 pointer-events-none select-none truncate",
            borderless ? "py-[0.15em] px-0" : "px-3 py-2",
            invalid ? "text-red-300" : "text-zinc-400",
            className,
          )}
          style={style}
        >
          {placeholder}
        </div>
      )}

      <div
        ref={(el) => {
          editorRef.current = el;
          if (typeof innerRef === "function") innerRef(el);
          else if (innerRef) innerRef.current = el;
          field.ref(el);
        }}
        contentEditable={!disabled}
        suppressContentEditableWarning
        role="textbox"
        aria-placeholder={placeholder}
        spellCheck={false}
        onInput={syncValue}
        onKeyDown={handleKeyDown}
        onClick={handleEditorClick}
        onCopy={handleCopy}
        onCut={handleCut}
        onPaste={handlePaste}
        onFocus={(e) => {
          onFocus?.(e);
          resetIdleTimer();
        }}
        onBlur={() => {
          if (dropdownOpen) return;
          field.onBlur();
        }}
        className={cn(
          "relative w-full outline-none cursor-text",
          wrap ? "break-words" : "whitespace-nowrap overflow-hidden",
          borderless
            ? "py-[0.15em] px-0 border-none"
            : cn(
                "px-3 py-2 -outline-offset-2 rounded-md focus:outline focus:outline-1 focus:border-transparent",
                disabled
                  ? "border border-zinc-200 bg-zinc-50 text-zinc-500 cursor-not-allowed"
                  : invalid
                    ? "border border-red-300 focus:outline-red-600"
                    : "border border-zinc-300 focus:outline-primary",
              ),
          className,
        )}
        style={style}
      />

      {availableTokens.length > 0 && (
        <TokenIdleButton
          editorRef={editorRef}
          containerRef={containerRef}
          dropdownOpen={dropdownOpen}
          onActivate={handleIdleButtonActivate}
        />
      )}

      {dropdownOpen && availableTokens.length > 0 && (
        <TokenDropdown
          tokens={availableTokens}
          onSelect={insertToken}
          onClose={() => {
            setDropdownOpen(false);
            savedRangeRef.current = null;
            replaceTargetRef.current = null;
            editorRef.current?.focus();
          }}
          caretPos={caretPos}
          openAbove={caretPos.viewportTop > window.innerHeight / 2}
          alignRight={caretPos.left + 256 > (containerRef.current?.offsetWidth ?? 0)}
        />
      )}
    </div>
  );
};

export default TemplateInput;
